import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  B2BIdentityProvider,
  parseOAuthAuthorizeParams,
  StytchAPIError,
  useStytchB2BClient,
  type OAuthAuthorizeParams,
} from '@stytch/react/b2b';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { AuthBrandLockup } from '@/components/AuthBrandLockup';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { publishedIssuer, withPublishedIssuer } from '@/auth/oauthIssuer';
import { isDemoMode } from '@/lib/demoMode';
import { logger } from '@/lib/logger';
import { sanitizeNextPath } from '@/lib/nextPath';

/** Scopes we write our own consent line for; any other shows Stytch's text. */
const KNOWN_SCOPES = ['openid', 'email', 'profile'] as const;
type KnownScope = (typeof KNOWN_SCOPES)[number];

function isKnownScope(scope: string): scope is KnownScope {
  return (KNOWN_SCOPES as readonly string[]).includes(scope);
}

type ScopeResult = { scope: string; description: string };

function scopeLine(t: TFunction, { scope, description }: ScopeResult) {
  if (isKnownScope(scope)) return t(`auth.oauthConsent.scopes.${scope}`);
  return description || scope;
}

/**
 * Who is asking, as the prebuilt screen names it: the connected app's name,
 * else the host its callback goes to, else "This app".
 */
function clientDisplayName(
  t: TFunction,
  clientName: string,
  redirectUri: string,
) {
  if (clientName) return clientName;
  try {
    const host = new URL(redirectUri).hostname;
    if (host) return host;
  } catch {
    // Not a URL we can read a host from; fall through to the generic name.
  }
  return t('auth.oauthConsent.unknownApp');
}

type ConsentState =
  | { status: 'loading' }
  | { status: 'error'; detail: string }
  | {
      status: 'consent';
      clientName: string;
      granted: string[];
      notGranted: string[];
    }
  | { status: 'redirecting' }
  | { status: 'done' };

function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="mb-8">
        <AuthBrandLockup />
      </div>
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">{children}</CardContent>
      </Card>
    </div>
  );
}

function StatusLine({ children }: { children: ReactNode }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className="text-center text-muted-foreground"
    >
      {children}
    </p>
  );
}

/**
 * Consent for an MCP client's OAuth login, on the headless Stytch B2B client.
 *
 * This replaced the prebuilt `<B2BIdentityProvider />` (ENG-2675) for one
 * reason: the prebuilt screen navigates to Stytch's `redirect_uri` verbatim,
 * and that URL's `iss` is Stytch's project issuer rather than the issuer our
 * AS metadata publishes. `withPublishedIssuer` fixes it before we navigate.
 * The flow is otherwise the prebuilt one: start → consent (skipped when Stytch
 * says none is needed) → submit → go to the returned `redirect_uri`, denial
 * included, so the client receives its `error=access_denied`.
 */
function OAuthConsent({ params }: { params: OAuthAuthorizeParams }) {
  const { t } = useTranslation();
  const stytch = useStytchB2BClient();
  const [state, setState] = useState<ConsentState>({ status: 'loading' });
  // StrictMode runs mount effects twice; a second start would auto-submit a
  // second time on a no-consent-needed request and mint a second code.
  const started = useRef(false);

  const fail = (what: string, error: unknown) => {
    logger.error(`OAuth consent ${what} failed`, error);
    setState({
      status: 'error',
      detail: StytchAPIError.from(error).error_message,
    });
  };

  const submit = async (consentGranted: boolean) => {
    setState({ status: 'redirecting' });
    try {
      const response = await stytch.idp.oauthAuthorizeSubmit({
        ...params,
        consent_granted: consentGranted,
      });
      window.location.assign(
        withPublishedIssuer(response.redirect_uri, publishedIssuer()),
      );
      // A localhost callback replaces this page; a custom-scheme one (an app
      // that opens itself) leaves the tab where it is, with nothing left to do.
      setState({ status: 'done' });
    } catch (error) {
      fail('submit', error);
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        const response = await stytch.idp.oauthAuthorizeStart(params);
        if (!response.consent_required) {
          // Same as the prebuilt screen: nothing to ask, so submit straight
          // away. Stytch ignores `consent_granted` when consent is not needed.
          await submit(false);
          return;
        }
        const results = response.scope_results;
        setState({
          status: 'consent',
          clientName: clientDisplayName(
            t,
            response.client.client_name,
            params.redirect_uri,
          ),
          granted: results
            .filter((r) => r.is_grantable)
            .map((r) => scopeLine(t, r)),
          notGranted: results
            .filter((r) => !r.is_grantable)
            .map((r) => scopeLine(t, r)),
        });
      } catch (error) {
        fail('start', error);
      }
    })();
    // Runs once per page: `params` is read from the URL a single time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  switch (state.status) {
    case 'loading':
      return (
        <PageShell>
          <StatusLine>{t('auth.oauthConsent.loading')}</StatusLine>
        </PageShell>
      );
    case 'redirecting':
      return (
        <PageShell>
          <StatusLine>{t('auth.oauthConsent.redirecting')}</StatusLine>
        </PageShell>
      );
    case 'done':
      return (
        <PageShell>
          <StatusLine>{t('auth.oauthConsent.done')}</StatusLine>
        </PageShell>
      );
    case 'error':
      return <ConsentError detail={state.detail} />;
  }

  const { clientName, granted, notGranted } = state;
  return (
    <PageShell>
      <h2 className="mb-4 text-center text-xl font-semibold text-foreground">
        {t('auth.oauthConsent.title', { clientName })}
      </h2>
      {granted.length > 0 && (
        <>
          <p className="mb-2 text-sm text-muted-foreground">
            {t('auth.oauthConsent.intro', { clientName })}
          </p>
          <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-foreground">
            {granted.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      )}
      {notGranted.length > 0 && (
        <p className="mb-4 text-sm text-muted-foreground">
          {t('auth.oauthConsent.notGranted', {
            scopes: notGranted.join(', '),
          })}
        </p>
      )}
      <div className="mt-6 flex flex-col gap-2">
        <Button type="button" onClick={() => void submit(true)}>
          {t('auth.oauthConsent.allow')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => void submit(false)}
        >
          {t('auth.oauthConsent.deny')}
        </Button>
      </div>
    </PageShell>
  );
}

function ConsentError({ detail }: { detail: string }) {
  const { t } = useTranslation();
  return (
    <PageShell>
      <h2 className="mb-2 text-center text-xl font-semibold text-foreground">
        {t('auth.oauthConsent.errorTitle')}
      </h2>
      <p className="text-center text-sm text-muted-foreground">
        {t('auth.oauthConsent.errorBody')}
      </p>
      {detail && (
        <p className="mt-4 text-center text-xs text-muted-foreground">
          {detail}
        </p>
      )}
    </PageShell>
  );
}

function OAuthAuthorizeRoute() {
  // Read the query once, on first render, as the prebuilt screen does.
  const [{ search, parsed }] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    return { search: query, parsed: parseOAuthAuthorizeParams(query) };
  });

  // The prebuilt screen also serves Stytch's OAuth logout flow (a request
  // carrying `post_logout_redirect_uri`). That response has no `iss` to fix,
  // so keep the prebuilt screen for it.
  if (search.has('post_logout_redirect_uri')) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <B2BIdentityProvider />
      </div>
    );
  }

  // Stytch's parser names the missing parameter (`client_id`/`redirect_uri`).
  if (parsed.error) return <ConsentError detail={parsed.error} />;

  return <OAuthConsent params={parsed.result} />;
}

export const Route = createFileRoute('/oauth/authorize')({
  beforeLoad: ({ context, location }) => {
    if (isDemoMode()) throw redirect({ to: '/' });
    if (!context.auth.isLoading && !context.auth.isAuthenticated) {
      throw redirect({
        to: '/login',
        search: { next: sanitizeNextPath(location.href) },
      });
    }
  },
  component: OAuthAuthorizeRoute,
});
